import { useMemo } from 'react';

import { Download } from '@mui/icons-material';
import { Button, Link, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { FidcComparatorTableItem } from 'src/services/fidcComparator';

import {
  buildFidcComparisonColumnLabel,
  buildFidcComparisonItemColumnKey,
  buildFidcComparisonTableRows,
  dedupeFidcComparisonData,
  exportFidcComparisonRowsToCsv,
  FidcComparisonTableRow,
  ND,
} from './config';

interface FidcComparisonTableProps {
  data: FidcComparatorTableItem[];
  loading: boolean;
}

const renderCellValue = (value: string) => {
  if (value === ND) {
    return (
      <Tooltip title="Dado não disponível para a competência selecionada">
        <Typography
          variant="body2"
          color="text.disabled"
          sx={(theme: Theme) => ({
            cursor: 'help',
            borderBottom: `1px dashed ${theme.palette.text.disabled}`,
            display: 'inline',
          })}>
          {ND}
        </Typography>
      </Tooltip>
    );
  }

  return <Typography variant="body2">{value}</Typography>;
};

export default function FidcComparisonTable({
  data,
  loading,
}: FidcComparisonTableProps) {
  const theme = useTheme();
  const uniqueData = useMemo(() => dedupeFidcComparisonData(data), [data]);
  const tableRows = useMemo(
    () => buildFidcComparisonTableRows(uniqueData),
    [uniqueData],
  );

  const columns = useMemo<MRT_ColumnDef<FidcComparisonTableRow>[]>(
    () => [
      {
        id: 'label',
        accessorKey: 'label',
        header: 'Indicador',
        minSize: 220,
        enableGrouping: false,
        muiTableHeadCellProps: {
          sx: { minWidth: 220 },
        },
        muiTableBodyCellProps: {
          sx: { minWidth: 220 },
        },
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      ...uniqueData.map<MRT_ColumnDef<FidcComparisonTableRow>>(
        (item, index) => ({
          id: buildFidcComparisonItemColumnKey(item, index),
          header: buildFidcComparisonColumnLabel(item),
          minSize: 220,
          enableGrouping: false,
          muiTableHeadCellProps: {
            align: 'center',
            sx: { minWidth: 220 },
          },
          muiTableBodyCellProps: {
            align: 'center',
            sx: { minWidth: 220 },
          },
          Header: () =>
            item.slug && item.operationId ? (
              <Link
                href={`/operacao/fidc/${item.slug}/${item.operationId}`}
                variant="body2"
                fontWeight={700}
                underline="hover"
                color="primary"
                textAlign="center"
                display="block">
                {buildFidcComparisonColumnLabel(item)}
              </Link>
            ) : (
              <Typography variant="body2" fontWeight={700} textAlign="center">
                {buildFidcComparisonColumnLabel(item)}
              </Typography>
            ),
          Cell: ({ row, column }) =>
            renderCellValue(row.original.values[column.id]),
        }),
      ),
    ],
    [uniqueData],
  );

  const table = useMaterialReactTable({
    columns,
    data: tableRows,
    enablePagination: false,
    enableBottomToolbar: false,
    initialState: {
      density: 'comfortable',
    },
    localization: {
      ...MRT_Localization_PT_BR,
      noRecordsToDisplay: 'Nenhum resultado encontrado',
    },
    state: {
      isLoading: loading,
      showProgressBars: loading,
    },
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableFilters: false,
    enableTopToolbar: false,
    enableRowVirtualization: false,
    muiTableBodyRowProps: {
      sx: {
        cursor: 'pointer',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 700,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr:nth-of-type(even) > td': {
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
      },
    },
    muiTableBodyCellProps: () => ({
      sx: {
        verticalAlign: 'middle',
      },
    }),
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        overflow: 'hidden',
        borderColor: theme.palette.divider,
        boxShadow: 'none',
      }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={1.5}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}>
        <Stack gap={0.5}>
          <Typography variant="subtitle1" fontWeight={700}>
            Tabela comparativa
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Download fontSize="small" />}
          disabled={loading || uniqueData.length === 0}
          onClick={() => exportFidcComparisonRowsToCsv(uniqueData)}>
          Exportar CSV
        </Button>
      </Stack>

      <MaterialReactTable table={table} />
    </Paper>
  );
}
